import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/hizmetler/noropazarlama';
const pageUrl = `${siteUrl}${pagePath}`;

const neuroFAQItems = [
  {
    question: 'Nöropazarlama nedir?',
    answer:
      'Nöropazarlama, tüketici davranışını anlamak için nörobilim yöntemlerini kullanan bir araştırma disiplinidir. EEG, eye tracking ve biyometrik ölçümlerle insanların söylediğine değil, gerçek tepkilerine bakar.',
  },
  {
    question: 'Nöropazarlama anketlerden daha mı güvenilir?',
    answer:
      'Evet. İnsanlar anketlerde sosyal beğenilirlik etkisiyle gerçek düşüncelerini söylemeyebilir. Nöropazarlama, bilinçaltı tepkileri ölçerek daha objektif veri sağlar. İnsanların ne dediğini değil, ne hissettiğini anlamanızı sağlar.',
  },
  {
    question: 'EEG ve eye tracking testleri nasıl yapılıyor?',
    answer:
      'Katılımcılar kontrollü bir ortamda EEG başlığı ve eye tracking cihazıyla içeriklerinizi (reklam, web sitesi, ambalaj vb.) inceler. Beyin aktivitesi, göz hareketleri ve biyometrik veriler eş zamanlı olarak kaydedilir.',
  },
  {
    question: 'Nöropazarlama hangi durumlarda faydalıdır?',
    answer:
      'Reklam kreatif testleri, ambalaj tasarımı değerlendirme, web sitesi kullanıcı deneyimi optimizasyonu, marka algısı ölçümü ve fiyatlandırma stratejisi gibi karar gerektiren tüm pazarlama süreçlerinde faydalıdır.',
  },
  {
    question: 'Nöropazarlama testleri ne kadar sürer?',
    answer:
      'Testin kapsamına bağlı olarak veri toplama 1-3 gün, analiz ve raporlama ise 1-2 hafta sürer. BIFF Digital olarak testleri uzman nörolog danışmanlığında yürütüyoruz.',
  },
  {
    question: 'Nöropazarlama sadece büyük markalar için mi?',
    answer:
      'Hayır. Özellikle büyüme hedefi olan orta ölçekli markalar için nöropazarlama, sınırlı bütçeyle maksimum etki yaratmanın en etkili yoludur. Yanlış kreatife harcanan reklam bütçesi, test maliyetinden çok daha yüksektir.',
  },
];

const faqSchema = getFAQSchema(neuroFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Nöropazarlama & Davranışsal Analiz Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'EEG, eye tracking ve biyometrik ölçümlerle kreatiflerinizi bilimsel olarak test ediyoruz. BIFF Digital nöropazarlamayı uzman nörolog danışmanlığında yürütür.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Nöropazarlama & Davranışsal Analiz',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Nöropazarlama Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'EEG Ölçümü', description: 'Beyin aktivitesi ölçümüyle duygusal ve bilişsel tepki analizi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Eye Tracking', description: 'Göz hareketi takibiyle dikkat ve odaklanma analizi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Biyometrik Ölçüm', description: 'Kalp ritmi, cilt iletkenliği ve yüz ifadesi analizi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kreatif Testleri', description: 'Reklam, ambalaj, web sitesi ve içerik testleri' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: 'Nöropazarlama & Davranışsal Analiz', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function Noropazarlama() {
  useEffect(() => {
    document.title = 'Nöropazarlama & Davranışsal Analiz | EEG & Eye Tracking | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">HİZMETLER</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-8">
              Nöropazarlama
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-10" />
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-6">
              İnsanların Söylediğine Değil,<br />
              <span className="text-primary-500">Tepkisine Bakın.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl speakable-summary">
              İnsanlar anketlerde yalan söylemez. Ama her zaman gerçeği de söylemez. Nöropazarlama, EEG, eye tracking ve biyometrik ölçümlerle bilinçaltı tepkileri okuyarak size anketlerin veremeyeceği cevapları verir.
            </p>
          </div>
        </section>

        {/* Problem */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              İnsanlar Anketlerde Yalan Söylemez.<br />Ama Her Zaman Gerçeği de Söylemez.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Bir reklamı beğendiklerini söylerler ama satın almazlar. Bir ambalajı modern bulurlar ama rafta ellerini uzatmazlar. Web sitenizi kullanışlı bulurlar ama alışverişi yarıda bırakırlar.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed mb-3">
                İnsanlar ne hissettiklerini her zaman kelimelerle ifade edemez.
              </p>
              <p className="text-sm text-foreground-300 leading-relaxed">
                Ama beyinleri, gözleri ve bedenleri her şeyi anlatır. Biz de bu sinyalleri okuyor, ölçüyor ve stratejiye dönüştürüyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Bilimsel Ölçümle<br />Kreatif Optimizasyon
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-brain-line', title: 'EEG Ölçümü', desc: 'Beyin elektriksel aktivitesini ölçerek duygusal tepkileri, dikkat seviyesini ve bilişsel yükü analiz ediyoruz. Hangi anlarda kullanıcı bağ kuruyor, hangi anlarda kopuyor — milisaniye hassasiyetinde görüyoruz.' },
                { icon: 'ri-eye-line', title: 'Eye Tracking', desc: 'Göz hareketlerini takip ederek kullanıcının nereye baktığını, ne kadar süre odaklandığını ve hangi sırayla incelediğini haritalandırıyoruz. Görsel hiyerarşiyi veriye dayalı olarak optimize ediyoruz.' },
                { icon: 'ri-heart-pulse-line', title: 'Biyometrik Ölçüm', desc: 'Kalp atış hızı, cilt iletkenliği ve yüz ifadesi analiziyle duygusal uyarılma seviyesini ölçüyoruz. Kullanıcının ne zaman heyecanlandığını, ne zaman sıkıldığını objektif olarak tespit ediyoruz.' },
                { icon: 'ri-test-tube-line', title: 'Kreatif Testleri', desc: 'Reklam filmleri, display banner\'lar, web sitesi tasarımları, ambalajlar ve içerikleri bilimsel yöntemlerle test ediyoruz. Tahmin etmek yerine ölçüyor, en etkili versiyonu seçiyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nörolog Danışmanlığı */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-6 md:p-8 border border-white/5">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center shrink-0">
                  <i className="ri-user-star-line text-xl text-primary-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-1">Uzman Nörolog Danışmanlığı</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    Tüm nöropazarlama testlerimiz alanında uzman bir nörolog danışmanlığında yürütülür. Veri toplama protokolleri, sinyal işleme ve yorumlama süreçleri bilimsel standartlara uygun şekilde gerçekleştirilir. Bu bir pazarlama hilesi değil, bilimsel bir araştırma yöntemidir.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Süreç */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Test &rarr; Measure &rarr; Interpret<br />&rarr; Optimize
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Test Tasarımı', desc: 'Hangi kreatiflerin, hangi hipotezlerle test edileceğini belirliyoruz. Doğru soruyu sormazsanız, doğru cevabı alamazsınız.' },
                { step: '02', title: 'Veri Toplama', desc: 'Kontrollü ortamda EEG, eye tracking ve biyometrik sensörlerle eş zamanlı veri topluyoruz. Her katılımcıdan 10.000\'den fazla veri noktası elde ediyoruz.' },
                { step: '03', title: 'Analiz ve Yorumlama', desc: 'Ham veriyi anlamlı içgörülere dönüştürüyoruz. Duygusal yoğunluk haritaları, dikkat ısı haritaları ve bilişsel yük grafikleriyle sonuçları görselleştiriyoruz.' },
                { step: '04', title: 'Aksiyon Planı', desc: 'Test sonuçlarını stratejik önerilere dönüştürüyoruz. Hangi kreatifi seçmelisiniz, hangi sahneleri değiştirmelisiniz, hangi mesaj daha etkili — hepsi raporda.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={neuroFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="NÖROPAZARLAMA"
          sectionSummary="Nöropazarlama ve davranışsal analiz hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Tahmin etmek yerine<br />
                  <span className="text-primary-500">ölelim.</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Kreatiflerinizin gerçekten işe yarayıp yaramadığını bilimsel olarak test edelim. Yanlış kreatife harcanan reklam bütçesi, test maliyetinden katbekat yüksektir.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Nöropazarlama Testi Planla
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}